import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get, post } from "../../lib/request"

// Async thunk for fetching cadence by client ID
export const fetchcadenceByClientId = createAsyncThunk(
  "cadence/fetchcadenceByClientId",
  async ({ clientId }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId) {
        throw new Error("Client ID is required")
      }

      const data = await get(`social-network-cadence/clients/${clientId}`)

      return data
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch cadence")
    }
  }
)

// Async thunk for updating cadence target
export const updateCadenceTarget = createAsyncThunk(
  "cadence/updateCadenceTarget",
  async ({ clientId, social_network_type_id, target }, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const token = state.auth?.token

      if (!token) {
        throw new Error("Authentication required. Please login first.")
      }

      if (!clientId || !social_network_type_id || target === undefined) {
        throw new Error("clientId, social_network_type_id and target are required")
      }

      const url = `social-network-cadence/clients/${clientId}?social_network_type_id=${social_network_type_id}&target=${target}`
      
      const response = await post(url)

      return { clientId, social_network_type_id, target, response }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to update cadence target")
    }
  }
)

const initialState = {
  cadence: [],       // ✔ NOT pillars
  status: "idle",    // idle | loading | succeeded | failed
  error: null,
  fetchedForClientId: null,
}

const cadenceSlice = createSlice({
  name: "cadence",   // ✔ slice renamed
  initialState,
  reducers: {
    clearCadence: (state) => {
      state.cadence = []
      state.status = "idle"
      state.error = null
      state.fetchedForClientId = null
    },
    clearCadenceError: (state) => {
      state.error = null
    },
    resetCadenceOnLogout: (state) => {
      state.cadence = []
      state.status = "idle"
      state.error = null
      state.fetchedForClientId = null
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchcadenceByClientId.pending, (state) => {
        state.status = "loading"
        state.error = null
      })
      .addCase(fetchcadenceByClientId.fulfilled, (state, action) => {
        state.status = "succeeded"
        state.fetchedForClientId = action.meta.arg?.clientId ?? null
        const raw = action.payload
        state.cadence = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : []
      })
      .addCase(fetchcadenceByClientId.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to fetch cadence"
      })
      
      // Update cadence target
      .addCase(updateCadenceTarget.pending, (state) => {
        state.status = "loading"
      })
      .addCase(updateCadenceTarget.fulfilled, (state, action) => {
        state.status = "succeeded"
        const { social_network_type_id, target } = action.payload
        
        // Update the target in the cadence array
        const index = state.cadence.findIndex(item => 
          item.socialNetworkType?.id === social_network_type_id
        )
        if (index !== -1) {
          state.cadence[index].target = target
        }
      })
      .addCase(updateCadenceTarget.rejected, (state, action) => {
        state.status = "failed"
        state.error = action.payload || "Failed to update cadence target"
      })
  },
})

export const {
  clearCadence,
  clearCadenceError,
  resetCadenceOnLogout
} = cadenceSlice.actions

export default cadenceSlice.reducer
